import axios from 'axios';
import { toast } from 'react-toastify';

// Configuration de l'URL de base pour MS_Client
const API_BASE_URL = process.env.REACT_APP_BASEURL_CLIENT_BACKEND || 'http://localhost:5001';

// Instance Axios configurée
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000
});

// Intercepteur pour gérer les tokens d'authentification
apiClient.interceptors.request.use(
    config => {
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// Intercepteur pour gérer les erreurs
apiClient.interceptors.response.use(
    response => response,
    error => {
        if (error.response) {
            switch (error.response.status) {
                case 401:
                    // Token expiré ou invalide
                    localStorage.removeItem('access_token');
                    window.location.href = '/login';
                    break;
                case 404:
                    console.error('Resource not found:', error.response.data);
                    break;
                case 500:
                    console.warn('Backend service not available yet:', error.response.data);
                    break;
                default:
                    console.error('API Error:', error.response.data);
            }
        } else if (error.request) {
            console.warn('Backend server not reachable - check if MS_Client is running');
        }
        return Promise.reject(error);
    }
);

class CardTraceabilityService {
    /**
     * Récupérer la liste paginée des cartes avec filtres
     */
    async getCardsPaged({
        page = 1,
        pageSize = 20,
        countryCode = null,
        status = null,
        beneficiaryId = null,
        serialNumber = null,
        batchNumber = null,
        sortBy = 'CreationDate',
        sortDirection = 'desc'
    }) {
        try {
            const params = {
                page,
                pageSize,
                sortBy,
                sortDirection
            };

            // Ajouter les paramètres optionnels s'ils existent
            if (countryCode) params.countryCode = countryCode;
            if (status && status !== 'ALL') params.status = status;
            if (beneficiaryId) params.beneficiaryId = beneficiaryId;
            if (serialNumber) params.serialNumber = serialNumber;
            if (batchNumber) params.batchNumber = batchNumber;

            const response = await apiClient.get('/Card/list', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching cards:', error);
            throw error;
        }
    }

    /**
     * Récupérer les détails complets d'une carte
     */
    async getCardDetails(cardId) {
        try {
            const response = await apiClient.get(`/Card/${cardId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching card details:', error);
            throw error;
        }
    }

    /**
     * Récupérer le statut de production d'une carte
     */
    async getProductionStatus(cardId) {
        try {
            const response = await apiClient.get(`/Card/production-status/${cardId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching production status:', error);
            throw error;
        }
    }

    /**
     * Récupérer l'historique des statuts d'une carte
     */
    async getCardStatusHistory(cardId) {
        try {
            const response = await apiClient.get(`/Card/${cardId}/history`);
            return response.data;
        } catch (error) {
            console.error('Error fetching status history:', error);
            // Si l'endpoint n'existe pas, retourner un tableau vide
            if (error.response?.status === 404) {
                return [];
            }
            throw error;
        }
    }

    /**
     * Mettre à jour le statut d'une carte
     */
    async updateCardStatus(cardId, status, reason, updatedBy) {
        try {
            const response = await apiClient.put(`/Card/${cardId}/status`, {
                status,
                reason,
                updatedBy: updatedBy || localStorage.getItem('userName') || 'Admin'
            });

            if (response.data) {
                toast.success(`Statut de la carte mis à jour : ${status}`);
            }

            return response.data;
        } catch (error) {
            console.error('Error updating card status:', error);
            toast.error('Erreur lors de la mise à jour du statut');
            throw error;
        }
    }

    /**
     * Activer une carte
     */
    async activateCard(cardId, activationCode, activatedBy) {
        try {
            const response = await apiClient.post(`/Card/${cardId}/activate`, {
                activationCode,
                activatedBy: activatedBy || localStorage.getItem('userName') || 'Admin'
            });

            if (response.data.success) {
                toast.success('Carte activée avec succès');
            }

            return response.data;
        } catch (error) {
            console.error('Error activating card:', error);
            toast.error('Erreur lors de l\'activation de la carte');
            throw error;
        }
    }

    /**
     * Bloquer une carte
     */
    async blockCard(cardId, reason, isTemporary = false, blockedBy) {
        try {
            const response = await apiClient.post(`/Card/${cardId}/block`, {
                reason,
                isTemporary,
                blockedBy: blockedBy || localStorage.getItem('userName') || 'Admin'
            });

            if (response.data) {
                toast.success('Carte bloquée avec succès');
            }

            return response.data;
        } catch (error) {
            console.error('Error blocking card:', error);
            toast.error('Erreur lors du blocage de la carte');
            throw error;
        }
    }

    /**
     * Débloquer une carte
     */
    async unblockCard(cardId, reason, unblockedBy) {
        try {
            const response = await apiClient.post(`/Card/${cardId}/unblock`, {
                reason,
                unblockedBy: unblockedBy || localStorage.getItem('userName') || 'Admin'
            });

            if (response.data) {
                toast.success('Carte débloquée avec succès');
            }

            return response.data;
        } catch (error) {
            console.error('Error unblocking card:', error);
            toast.error('Erreur lors du déblocage de la carte');
            throw error;
        }
    }

    /**
     * Récupérer les lots de cartes
     */
    async getCardBatches(countryCode = null, pageNumber = 1, pageSize = 50) {
        try {
            const params = {
                pageNumber,
                pageSize
            };

            if (countryCode) params.countryCode = countryCode;

            const response = await apiClient.get('/Card/batches', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching card batches:', error);
            throw error;
        }
    }

    /**
     * Récupérer les informations de stock pour une carte
     */
    async getCardStockInfo(cardId) {
        try {
            const response = await apiClient.get(`/Stock/card/${cardId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching card stock info:', error);
            // Si l'endpoint n'existe pas, retourner null
            if (error.response?.status === 404) {
                return null;
            }
            throw error;
        }
    }

    /**
     * Récupérer les contrôles qualité d'une carte
     */
    async getCardQualityChecks(cardId) {
        try {
            const response = await apiClient.get(`/Card/${cardId}/quality-checks`);
            return response.data;
        } catch (error) {
            console.error('Error fetching quality checks:', error);
            // Si l'endpoint n'existe pas, retourner un tableau vide
            if (error.response?.status === 404) {
                return [];
            }
            throw error;
        }
    }

    /**
     * Récupérer les anomalies détectées
     */
    async getAnomalies(filters = {}) {
        try {
            const params = {
                page: filters.page || 1,
                pageSize: filters.pageSize || 50,
                severity: filters.severity,
                type: filters.type,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo
            };

            // Nettoyage des paramètres undefined
            Object.keys(params).forEach(key => {
                if (params[key] === undefined || params[key] === null) {
                    delete params[key];
                }
            });

            const response = await apiClient.get('/Card/anomalies', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching anomalies:', error);
            // Si l'endpoint n'existe pas, retourner des données vides
            if (error.response?.status === 404) {
                return { data: [], total: 0 };
            }
            throw error;
        }
    }

    /**
     * Récupérer les statistiques de production
     */
    async getProductionStatistics(countryCode = null, dateFrom = null, dateTo = null) {
        try {
            const params = {};
            if (countryCode) params.countryCode = countryCode;
            if (dateFrom) params.startDate = dateFrom;
            if (dateTo) params.endDate = dateTo;

            const response = await apiClient.get('/Card/statistics/production', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching production statistics:', error);
            // Retourner des statistiques par défaut si l'endpoint n'existe pas
            if (error.response?.status === 404) {
                return {
                    totalCards: 0,
                    byStatus: {},
                    averageProductionTime: 0,
                    qualityControlPassRate: 100,
                    defectiveRate: 0,
                    onTimeDeliveryRate: 100,
                    totalBatches: 0,
                    activeBatches: 0,
                    completedBatches: 0
                };
            }
            throw error;
        }
    }

    /**
     * Exporter les données de traçabilité
     */
    async exportTraceabilityReport(format = 'excel', filters = {}) {
        try {
            const params = {
                format,
                ...filters
            };

            const response = await apiClient.get('/Card/export', {
                params,
                responseType: 'blob'
            });

            // Créer un lien de téléchargement
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `traceability_report_${Date.now()}.${format === 'excel' ? 'xlsx' : format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success('Rapport exporté avec succès');
            return true;
        } catch (error) {
            console.error('Error exporting report:', error);
            toast.error('Erreur lors de l\'export du rapport');
            throw error;
        }
    }

    /**
     * Mapper les statuts de l'API vers le format attendu par l'interface
     */
    mapApiStatusToUI(apiStatus) {
        const statusMap = {
            'Created': 'Ordered',
            'Production': 'InProduction',
            'Printed': 'Printed',
            'QualityCheck': 'QualityControl',
            'Ready': 'ReadyForDelivery',
            'Shipped': 'Shipped',
            'Delivered': 'Delivered',
            'Active': 'Activated',
            'Blocked': 'Defective',
            'Suspended': 'Defective'
        };
        return statusMap[apiStatus] || apiStatus;
    }

    /**
     * Mapper les données de carte de l'API vers le format UI
     */
    mapCardDataToUI(apiCard) {
        return {
            cardId: apiCard.id,
            serialNumber: apiCard.cardNumber || apiCard.serialNumber,
            beneficiaryId: apiCard.beneficiaryId,
            beneficiaryName: apiCard.beneficiary?.fullName || `Bénéficiaire ${apiCard.beneficiaryId}`,
            currentStatus: this.mapApiStatusToUI(apiCard.status),
            creationDate: new Date(apiCard.createdAt || apiCard.creationDate),
            lastStatusChange: new Date(apiCard.lastStatusChangeDate || apiCard.updatedAt),
            totalStatusChanges: apiCard.statusHistory?.length || 0,
            timeInCurrentStatus: this.calculateDaysInStatus(apiCard.lastStatusChangeDate || apiCard.updatedAt),
            batchNumber: apiCard.batchNumber || apiCard.batch?.batchNumber || 'N/A',
            productionPhase: this.getProductionPhase(apiCard.status),
            hasAnomalies: apiCard.hasAnomalies || false,
            totalLifecycleDays: this.calculateLifecycleDays(apiCard.createdAt || apiCard.creationDate),
            printingDate: apiCard.printingDate ? new Date(apiCard.printingDate) : null,
            qualityControlPassed: apiCard.qualityControlPassed,
            deliveryDate: apiCard.deliveryDate ? new Date(apiCard.deliveryDate) : null,
            activationDate: apiCard.activationDate ? new Date(apiCard.activationDate) : null,
            isBlocked: apiCard.isBlocked || false,
            blockReason: apiCard.blockReason
        };
    }

    /**
     * Calculer le nombre de jours dans le statut actuel
     */
    calculateDaysInStatus(lastChangeDate) {
        if (!lastChangeDate) return 0;
        const now = new Date();
        const changeDate = new Date(lastChangeDate);
        const diffTime = Math.abs(now - changeDate);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    /**
     * Calculer la durée totale du cycle de vie
     */
    calculateLifecycleDays(creationDate) {
        if (!creationDate) return 0;
        const now = new Date();
        const created = new Date(creationDate);
        const diffTime = Math.abs(now - created);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    /**
     * Déterminer la phase de production
     */
    getProductionPhase(status) {
        const phaseMap = {
            'Created': 'Commande',
            'Production': 'Production',
            'Printed': 'Impression',
            'QualityCheck': 'Contrôle',
            'Ready': 'Expédition',
            'Shipped': 'Transport',
            'Delivered': 'Livré',
            'Active': 'Actif',
            'Blocked': 'Bloqué'
        };
        return phaseMap[status] || 'Inconnu';
    }
}

// Export d'une instance singleton
export const cardTraceabilityService = new CardTraceabilityService();
export default cardTraceabilityService;